/**
 * webrtc.ts — the console's WebRTC viewer engine (SPEC §4 + §9 + §10).
 *
 * Role: the browser is the ANSWERER. The agent is the offerer (SPEC §4 step 3).
 * The agent adds an H.264 `recvonly` video track and *opens* the data channels;
 * the console attaches handlers to the channels it receives via `ondatachannel`.
 *
 * Responsibilities:
 *   - Build RTCPeerConnection with the session's TURN creds (turns:443 first).
 *   - Drive SDP answer + trickle ICE through the SignalingClient.
 *   - Surface the inbound H.264 media track to the caller (→ <video>).
 *   - Expose the six SPEC §9 data channels once the agent opens them.
 *   - Run the §10 E2EE handshake on `control` before input/clipboard/files flow.
 *   - Install the media decrypt transform (§10.3 hook).
 *   - Sample getStats() for the connection-quality panel.
 */

import type {
  ChannelLabel,
  ConnStats,
  ControlMessage,
  IceCandidatePayload,
  SdpPayload,
  SignalEnvelope,
  TurnCredentials,
} from '@/types';
import { CHANNELS } from '@/types';
import { SignalingClient } from '@/lib/signaling';
import { base64ToBytes, bytesToBase64 } from '@/lib/binary';
import {
  attachMediaDecryptor,
  NoiseInitiator,
  openDataChecked,
  type SessionKeys,
} from '@/lib/e2ee';

export interface WebRTCViewerOptions {
  signaling: SignalingClient;
  turn: TurnCredentials;
  /**
   * The agent's pinned Noise static public key (hex, 32 bytes) — the Noise_XK
   * responder static (SPEC §10.1). The console is the initiator and MUST know
   * this before it can run the handshake. Sourced from the device detail
   * (last_sysinfo.noise_pubkey). If absent, E2EE cannot start and channels stay
   * unusable (fail closed, SPEC §11).
   */
  agentNoisePubKeyHex?: string;
  /** Called when the inbound H.264 media track arrives. */
  onTrack: (stream: MediaStream) => void;
  /** Called when a data channel opens; consumers bind their own handlers. */
  onChannel: (label: ChannelLabel, ch: RTCDataChannel) => void;
  /** Parsed control-channel messages (monitor list, cursor, consent, stats). */
  onControl?: (msg: ControlMessage) => void;
  /** Connection-quality updates (~1 Hz). */
  onStats?: (stats: ConnStats) => void;
  /** E2EE handshake completed; data channels are now safe to use. */
  onSecure?: () => void;
  /**
   * High-level connection lifecycle for the UI: 'connecting' on (re)negotiation,
   * 'connected' once the peer connection is up, 'reconnecting' when the transport
   * drops (signaling reconnect or ICE disconnect), 'failed' on terminal failure.
   */
  onConnState?: (state: 'connecting' | 'connected' | 'reconnecting' | 'failed') => void;
}

export class WebRTCViewer {
  private pc: RTCPeerConnection;
  private readonly channels = new Map<ChannelLabel, RTCDataChannel>();
  private keys: SessionKeys | null = null;
  private statsTimer: ReturnType<typeof setInterval> | null = null;
  private lastBytes = 0;
  private lastTs = 0;
  private offsignal: Array<() => void> = [];
  private closed = false;

  constructor(private readonly opts: WebRTCViewerOptions) {
    this.pc = new RTCPeerConnection({
      // turns:443 first (firewall-friendly), per SPEC §4/§5. Defaults to relay
      // fallback while preferring P2P (architecture §4.2: P2P-preferred v1).
      iceServers: [
        {
          urls: opts.turn.urls,
          username: opts.turn.username,
          credential: opts.turn.credential,
        },
      ],
      iceTransportPolicy: 'all',
      bundlePolicy: 'max-bundle',
    });
    this.wirePeer();
    this.wireSignaling();
  }

  /** Begin: the agent is already connected; nudge it by signalling hello/ready. */
  start(): void {
    // Browser is the answerer: we wait for the agent's sdp_offer. Nothing to do
    // here except ensure the signaling socket is live (caller connects it).
    this.opts.onConnState?.('connecting');
    this.startStatsLoop();
  }

  private wirePeer(): void {
    this.pc.ontrack = (ev) => {
      // recvonly H.264 video track from the agent (SPEC §9).
      const [stream] = ev.streams;
      if (stream) this.opts.onTrack(stream);
      // Install the media decrypt transform on the receiver (§10.3 hook).
      try {
        attachMediaDecryptor(ev.receiver, () => this.keys);
      } catch {
        /* Encoded Transform unsupported on this browser; media stays as-is. */
      }
    };

    this.pc.ondatachannel = (ev) => {
      const label = ev.channel.label as ChannelLabel;
      this.bindChannel(label, ev.channel);
    };

    this.pc.onicecandidate = (ev) => {
      if (ev.candidate) {
        const c: IceCandidatePayload = {
          candidate: ev.candidate.candidate,
          sdpMid: ev.candidate.sdpMid,
          sdpMLineIndex: ev.candidate.sdpMLineIndex,
        };
        this.opts.signaling.sendIce(c);
      }
    };

    this.pc.onconnectionstatechange = () => {
      void this.emitStats();
      switch (this.pc.connectionState) {
        case 'connected':
          this.opts.onConnState?.('connected');
          break;
        case 'disconnected':
          this.opts.onConnState?.('reconnecting');
          break;
        case 'failed':
          // ICE failed: attempt one ICE restart by re-offering readiness; the
          // agent (offerer) re-negotiates. If the socket is also down, the
          // signaling client's reconnect drives recovery.
          this.opts.onConnState?.('reconnecting');
          this.tryIceRestart();
          break;
        case 'closed':
          break;
        default:
          break;
      }
    };
    this.pc.oniceconnectionstatechange = () => this.emitStats();
  }

  private wireSignaling(): void {
    const s = this.opts.signaling;
    this.offsignal.push(
      s.on('sdp_offer', (env) => void this.onOffer(env)),
      s.on('ice_candidate', (env) => void this.onRemoteIce(env)),
      s.on('session_end', () => this.close()),
    );
  }

  /**
   * Ask the agent to renegotiate after a transport drop. The browser is the
   * answerer, so it cannot create its own offer; it requests a fresh offer +
   * keyframe over the (reconnected) control channel. If control is down the
   * agent re-offers on its own once the signaling socket is back.
   */
  private tryIceRestart(): void {
    if (this.closed) return;
    this.opts.onConnState?.('reconnecting');
    // Request a fresh IDR over control (agent proto.CtrlKeyframe).
    this.sendControl({ kind: 'keyframe' });
  }

  /** Called by the caller when the signaling socket reconnects. */
  onSignalingReconnect(): void {
    if (this.closed) return;
    this.tryIceRestart();
  }

  private async onOffer(env: SignalEnvelope): Promise<void> {
    const payload = env.payload as SdpPayload;
    await this.pc.setRemoteDescription({ type: 'offer', sdp: payload.sdp });
    const answer = await this.pc.createAnswer();
    // Bias the answer toward H.264 (SPEC §9: H.264 constrained-baseline/main).
    answer.sdp = preferH264(answer.sdp ?? '');
    await this.pc.setLocalDescription(answer);
    this.opts.signaling.sendAnswer({ sdp: answer.sdp ?? '', type: 'answer' });
  }

  private async onRemoteIce(env: SignalEnvelope): Promise<void> {
    const c = env.payload as IceCandidatePayload;
    try {
      await this.pc.addIceCandidate({
        candidate: c.candidate,
        sdpMid: c.sdpMid ?? undefined,
        sdpMLineIndex: c.sdpMLineIndex ?? undefined,
      });
    } catch {
      /* candidate may arrive before remote description; browser buffers most. */
    }
  }

  private bindChannel(label: ChannelLabel, ch: RTCDataChannel): void {
    ch.binaryType = 'arraybuffer';
    this.channels.set(label, ch);

    if (label === CHANNELS.control) {
      ch.onmessage = (ev) => this.onControlMessage(ev);
    }

    ch.onopen = () => {
      if (label === CHANNELS.control) {
        void this.beginE2EE(ch);
      }
      this.opts.onChannel(label, ch);
    };
  }

  // ----- §10 E2EE handshake (runs on `control` before input/clipboard/files) --
  // The console is the Noise_XK INITIATOR; the agent is the responder whose
  // static key is pinned (agentNoisePubKeyHex). Wire matches agent proto.ControlMsg
  // exactly: handshake bytes are base64 in the `b64` field.
  //   -> e2ee_init  (msg1)  ;  <- e2ee_resp (msg2)  ;  -> e2ee_final (msg3)
  private hs: NoiseInitiator | null = null;

  private async beginE2EE(control: RTCDataChannel): Promise<void> {
    const hex = this.opts.agentNoisePubKeyHex;
    if (!hex) {
      // No pinned agent static key → cannot run XK. Fail closed (SPEC §11).
      return;
    }
    try {
      const rs = hexToBytes(hex);
      this.hs = await NoiseInitiator.create(rs);
      const msg1 = await this.hs.writeMsg1();
      const init: ControlMessage = { kind: 'e2ee_init', b64: bytesToBase64(msg1) };
      control.send(JSON.stringify(init));
    } catch {
      this.hs = null;
      this.keys = null;
    }
  }

  private async onControlMessage(ev: MessageEvent): Promise<void> {
    // Handshake frames are cleartext JSON; post-handshake control metadata may be
    // E2EE-sealed by the agent. We try a cleartext parse first; if that yields a
    // known handshake/structured kind we use it, otherwise (once keys exist) we
    // unseal and parse the inner JSON.
    const raw =
      typeof ev.data === 'string' ? ev.data : new TextDecoder().decode(ev.data as ArrayBuffer);
    let msg: ControlMessage | null = parseControl(raw);

    if (msg && (msg.kind === 'e2ee_resp' || msg.kind === 'e2ee_init' || msg.kind === 'e2ee_final')) {
      await this.onHandshake(msg);
      return;
    }

    // Sealed structured control frame (monitors/cursor/consent after handshake).
    if (!msg && this.keys && ev.data instanceof ArrayBuffer) {
      try {
        const clear = openDataChecked(this.keys, new Uint8Array(ev.data));
        msg = parseControl(new TextDecoder().decode(clear));
      } catch {
        /* not a sealed control frame */
      }
    }
    if (msg) this.opts.onControl?.(msg);
  }

  private async onHandshake(msg: ControlMessage): Promise<void> {
    if (!this.hs) return;
    try {
      if (msg.kind === 'e2ee_resp') {
        const msg2 = base64ToBytes(msg.b64 ?? '');
        await this.hs.readMsg2(msg2);
        const msg3 = await this.hs.writeMsg3();
        this.sendControlRaw({ kind: 'e2ee_final', b64: bytesToBase64(msg3) });
        this.keys = await this.hs.session();
        this.hs = null;
        this.opts.onSecure?.();
      }
    } catch {
      // Handshake failure → keep channels unusable (fail closed, SPEC §11).
      this.hs = null;
      this.keys = null;
    }
  }

  /** Session keys for callers that seal/open data-channel payloads (input/files). */
  sessionKeys(): SessionKeys | null {
    return this.keys;
  }

  channel(label: ChannelLabel): RTCDataChannel | undefined {
    return this.channels.get(label);
  }

  // sendControl sends a control-channel message as cleartext JSON. The agent's
  // control reader (proto.ControlMsg) parses control frames directly without
  // unsealing, so tech→agent control (set_monitor/keyframe/e2ee_final) is always
  // cleartext. Sensitive data never flows on `control`; it goes on the sealed
  // input/clipboard/shell/files channels after the handshake.
  sendControl(msg: ControlMessage): void {
    this.sendControlRaw(msg);
  }

  private sendControlRaw(msg: ControlMessage): void {
    const ch = this.channels.get(CHANNELS.control);
    if (ch?.readyState === 'open') ch.send(JSON.stringify(msg));
  }

  // ----- connection stats (getStats) ---------------------------------------
  private startStatsLoop(): void {
    this.statsTimer = setInterval(() => void this.emitStats(), 1000);
  }

  private async emitStats(): Promise<void> {
    if (!this.opts.onStats || this.closed) return;
    const base: ConnStats = {
      state: this.pc.connectionState,
      iceState: this.pc.iceConnectionState,
    };
    try {
      const report = await this.pc.getStats();
      let candidatePair: ConnStats['candidatePair'] = 'unknown';
      report.forEach((r) => {
        if (r.type === 'inbound-rtp' && (r as RTCInboundRtpStreamStats).kind === 'video') {
          const s = r as RTCInboundRtpStreamStats & {
            bytesReceived?: number;
            framesPerSecond?: number;
            frameWidth?: number;
            frameHeight?: number;
            jitter?: number;
            packetsLost?: number;
          };
          const now = performance.now();
          if (this.lastTs && s.bytesReceived !== undefined) {
            const dBytes = s.bytesReceived - this.lastBytes;
            const dSec = (now - this.lastTs) / 1000;
            if (dSec > 0) base.bitrateKbps = Math.max(0, Math.round((dBytes * 8) / dSec / 1000));
          }
          this.lastBytes = s.bytesReceived ?? this.lastBytes;
          this.lastTs = now;
          base.fps = s.framesPerSecond;
          base.frameWidth = s.frameWidth;
          base.frameHeight = s.frameHeight;
          base.jitterMs = s.jitter !== undefined ? Math.round(s.jitter * 1000) : undefined;
          base.packetsLost = s.packetsLost;
        }
        if (r.type === 'candidate-pair' && (r as RTCIceCandidatePairStats).nominated) {
          const cp = r as RTCIceCandidatePairStats & { currentRoundTripTime?: number };
          if (cp.currentRoundTripTime !== undefined) {
            base.rttMs = Math.round(cp.currentRoundTripTime * 1000);
          }
          const local = report.get((cp as { localCandidateId?: string }).localCandidateId ?? '') as
            | { candidateType?: string }
            | undefined;
          const t = local?.candidateType;
          if (t === 'host' || t === 'srflx' || t === 'prflx' || t === 'relay') candidatePair = t;
        }
      });
      base.candidatePair = candidatePair;
    } catch {
      /* getStats can throw during teardown */
    }
    this.opts.onStats(base);
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    if (this.statsTimer) clearInterval(this.statsTimer);
    for (const off of this.offsignal) off();
    for (const ch of this.channels.values()) {
      try {
        ch.close();
      } catch {
        /* ignore */
      }
    }
    this.channels.clear();
    try {
      this.pc.close();
    } catch {
      /* ignore */
    }
  }
}

// ---------------------------------------------------------------------------
// SDP helper: move H.264 payload types to the front of the m=video line so the
// browser negotiates H.264 (SPEC §9: H.264 constrained-baseline/main).
// ---------------------------------------------------------------------------
export function preferH264(sdp: string): string {
  const lines = sdp.split(/\r?\n/);
  const mIndex = lines.findIndex((l) => l.startsWith('m=video'));
  if (mIndex === -1) return sdp;

  const h264Pts = new Set<string>();
  for (const line of lines) {
    const m = /^a=rtpmap:(\d+)\s+H264\/90000/i.exec(line);
    if (m) h264Pts.add(m[1]);
  }
  if (h264Pts.size === 0) return sdp;

  const parts = lines[mIndex].split(' ');
  const header = parts.slice(0, 3); // m=video <port> <proto>
  const pts = parts.slice(3);
  const reordered = [
    ...pts.filter((p) => h264Pts.has(p)),
    ...pts.filter((p) => !h264Pts.has(p)),
  ];
  lines[mIndex] = [...header, ...reordered].join(' ');
  return lines.join('\r\n');
}

// ---------------------------------------------------------------------------
// control parsing + hex helpers
// ---------------------------------------------------------------------------

/** Parse a control-channel JSON string into a ControlMessage, or null. */
function parseControl(text: string): ControlMessage | null {
  try {
    const obj = JSON.parse(text) as Partial<ControlMessage>;
    if (obj && typeof obj.kind === 'string') return obj as ControlMessage;
  } catch {
    /* not JSON */
  }
  return null;
}

/** Decode a hex string (agent Noise static pubkey) to bytes. */
function hexToBytes(hex: string): Uint8Array {
  const clean = hex.trim();
  if (clean.length % 2 !== 0) throw new Error('e2ee: odd-length hex');
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(clean.substr(i * 2, 2), 16);
  }
  return out;
}
